import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useBeforeUnload, useBlocker } from "react-router-dom";
import { useDispatch } from "react-redux";
import IconButton from "@shared/v2/IconButton";
import {
  ArrowLeftSolidV6,
  CheckSolidV6,
  SpinnerThirdSolidV6,
  TriangleExclamationSolidV6,
} from "@shared/v2/Icomoon";
import axios from "axios";
import AreaOverviewPanel from "./AreaOverviewPanel";
import ContactFormPanel from "./ContactFormPanel";
import DetailsPanel from "./DetailsPanel";
import FeaturedImagePanel from "./FeaturedImagePanel";
import LinksPanel from "./LinksPanel";
import MarketStatsPanel from "./MarketStatsPanel";
import PublishPanel from "./PublishPanel";
import SeoPanel from "./SeoPanel";
import UnsavedChangesModal from "./UnsavedChangesModal";
import Loading from "../../Goals/components/Loader";
import { setSnackbar } from "../../reducers/snackbarReducer";
import {
  getToken,
  getSites,
  getPage,
  getPageSettings,
  getComponents,
  getPageContentComponents,
  updateContentComponent,
  updatePageSettings,
  updatePage,
  updateContentComponentBindings,
  publish,
} from "../../reducers/websiteReducer/areaPageReducer";
import {
  usePage,
  useAreaOverviewComponent,
  useMarketStatsComponent,
  useLinksComponent,
  useFeaturedListingsComponent,
  useContactCTAComponent,
  useFeaturedImageComponent,
  usePageSettings,
  useSiteUuid,
  useToken,
  useTokenExpiresAt,
} from "../../reducers/websiteReducer/areaPageSelectors";
import FeaturedListingsPanel from "./FeaturedListingsPanel";
import ThumbnailPanel from "./ThumbnailPanel";
import { isTokenValid } from "./api";

// Plain text stored in Page Maker gets wrapped in <p> by TinyMCE on load.
// Normalize here so originalData and post-undo getContent() use the same format.
const normalizeBody = (body) => {
  if (!body || /^\s*</.test(body)) return body;
  return `<p>${body}</p>`;
};

const withSeoTitleFallback = (pageSnapshot) => ({
  ...pageSnapshot,
  seoTitle: pageSnapshot?.seoTitle?.trim() || pageSnapshot?.name,
});

const AreaPageEditor = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { uuid } = useParams();

  const token = useToken();
  const tokenExpiresAt = useTokenExpiresAt();
  const siteUuid = useSiteUuid();
  const page = usePage();
  const pageSettings = usePageSettings();
  const areaOverviewComponent = useAreaOverviewComponent();
  const marketStatsComponent = useMarketStatsComponent();
  const linksComponent = useLinksComponent();
  const featuredListingsComponent = useFeaturedListingsComponent();
  const contactCTAComponent = useContactCTAComponent();
  const featuredImageComponent = useFeaturedImageComponent();

  const [areaOverviewData, setAreaOverviewData] = useState(null);
  const [marketStatsData, setMarketStatsData] = useState(null);
  const [linksData, setLinksData] = useState(null);
  const [featuredListingsData, setFeaturedListingsData] = useState(null);
  const [contactCTAData, setContactCTAData] = useState(null);
  const [featuredImageData, setFeaturedImageData] = useState(null);
  const [pageData, setPageData] = useState(null);

  const [originalAreaOverviewData, setOriginalAreaOverviewData] = useState(null);
  const [originalMarketStatsData, setOriginalMarketStatsData] = useState(null);
  const [originalLinksData, setOriginalLinksData] = useState(null);
  const [originalFeaturedListingsData, setOriginalFeaturedListingsData] = useState(null);
  const [originalContactCTAData, setOriginalContactCTAData] = useState(null);
  const [originalFeaturedImageData, setOriginalFeaturedImageData] = useState(null);
  const [originalPageData, setOriginalPageData] = useState(null);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const deepEqual = (a, b) => {
    // Same reference or same primitive value
    if (Object.is(a, b)) {
      return true;
    }

    // One is null, or types don't match
    if (a === null || b === null || typeof a !== typeof b) {
      return false;
    }

    // Arrays — order matters
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return false;
      }

      return a.every((item, i) => deepEqual(item, b[i]));
    }

    // One is an array and the other isn't
    if (Array.isArray(a) || Array.isArray(b)) {
      return false;
    }

    // Objects
    if (typeof a === "object" && typeof b === "object") {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) {
        return false;
      }

      return keysA.every((key) => Object.hasOwn(b, key) && deepEqual(a[key], b[key]));
    }

    return false;
  };

  const areaOverviewHasChanges = useMemo(
    () => !deepEqual(areaOverviewData, originalAreaOverviewData),
    [areaOverviewData, originalAreaOverviewData],
  );
  const marketStatsHasChanges = useMemo(
    () => !deepEqual(marketStatsData, originalMarketStatsData),
    [marketStatsData, originalMarketStatsData],
  );

  const featuredListingsHasChanges = useMemo(
    () => !deepEqual(featuredListingsData, originalFeaturedListingsData),
    [featuredListingsData, originalFeaturedListingsData],
  );
  const linksHasChanges = useMemo(
    () => !deepEqual(linksData, originalLinksData),
    [linksData, originalLinksData],
  );
  const contactCTADataHasChanges = useMemo(
    () => !deepEqual(contactCTAData, originalContactCTAData),
    [contactCTAData, originalContactCTAData],
  );

  const featuredImageHasChanges = useMemo(
    () => !deepEqual(featuredImageData, originalFeaturedImageData),
    [featuredImageData, originalFeaturedImageData],
  );

  const pageDataHasChanges = useMemo(
    () => !deepEqual(pageData, originalPageData),
    [pageData, originalPageData],
  );

  const hasChanges =
    areaOverviewHasChanges ||
    marketStatsHasChanges ||
    linksHasChanges ||
    featuredListingsHasChanges ||
    contactCTADataHasChanges ||
    featuredImageHasChanges ||
    pageDataHasChanges;

  useEffect(() => {
    const setup = async () => {
      if (!isTokenValid(token, tokenExpiresAt)) {
        await dispatch(getToken()).unwrap();
      }
      if (!siteUuid) await dispatch(getSites()).unwrap();
    };
    setup();
  }, []);

  useEffect(() => {
    if (!siteUuid) return undefined;
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        await dispatch(getPage({ uuid })).unwrap();
        if (!active) return;
        await dispatch(getPageSettings({ uuid })).unwrap();
        if (!active) return;
        await dispatch(getComponents()).unwrap();
        if (!active) return;
        await dispatch(getPageContentComponents()).unwrap();
      } catch {
        dispatch(
          setSnackbar({
            show: true,
            position: "top",
            text: "Error loading page",
            schema: "error",
            timeout: 5000,
            closeOnTimeout: () => dispatch(setSnackbar({ show: false })),
          }),
        );
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [uuid, siteUuid]);

  useEffect(() => {
    setAreaOverviewData(null);
    setOriginalAreaOverviewData(null);
    setMarketStatsData(null);
    setOriginalMarketStatsData(null);
    setLinksData(null);
    setOriginalLinksData(null);
    setFeaturedListingsData(null);
    setOriginalFeaturedListingsData(null);
    setContactCTAData(null);
    setOriginalContactCTAData(null);
    setFeaturedImageData(null);
    setOriginalFeaturedImageData(null);
    setPageData(null);
    setOriginalPageData(null);
  }, [uuid]);

  useEffect(() => {
    const normalized = areaOverviewComponent
      ? { ...areaOverviewComponent, body: normalizeBody(areaOverviewComponent.body) }
      : areaOverviewComponent;
    setAreaOverviewData(normalized);
    setOriginalAreaOverviewData(normalized);
  }, [areaOverviewComponent]);

  useEffect(() => {
    if (marketStatsComponent) {
      const normalized = { ...marketStatsComponent, body: normalizeBody(marketStatsComponent.body) };
      setMarketStatsData(normalized);
      setOriginalMarketStatsData(normalized);
    }
  }, [marketStatsComponent]);

  useEffect(() => {
    setLinksData(linksComponent);
    setOriginalLinksData(linksComponent);
  }, [linksComponent]);

  useEffect(() => {
    setFeaturedListingsData(featuredListingsComponent);
    setOriginalFeaturedListingsData(featuredListingsComponent);
  }, [featuredListingsComponent]);

  useEffect(() => {
    setContactCTAData(contactCTAComponent);
    setOriginalContactCTAData(contactCTAComponent);
  }, [contactCTAComponent]);

  useEffect(() => {
    setFeaturedImageData(featuredImageComponent);
    setOriginalFeaturedImageData(featuredImageComponent);
  }, [featuredImageComponent]);

  useEffect(() => {
    setPageData({ ...page, ...pageSettings });
    setOriginalPageData({ ...page, ...pageSettings });
  }, [page, pageSettings]);

  // useEffect(() => {
  //   if (areaSelection && featuredImageComponent) {
  //     saveFeaturedImage();
  //   }
  // }, [areaSelection, featuredImageComponent]);

  // Browser navigation (tab close / reload)
  useBeforeUnload(
    useCallback(
      (e) => {
        if (hasChanges) e.returnValue = "You have made changes to this page without saving.";
      },
      [hasChanges],
    ),
    { capture: true },
  );

  // Internal navigation (router); powers the Unsaved Changes modal
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasChanges && currentLocation.pathname !== nextLocation.pathname,
  );

  const saveAreaOverview = async () => {
    const payload = {
      isActive: areaOverviewData.isActive,
      content: {
        body: areaOverviewData.body,
        eyebrow: areaOverviewData.eyebrow,
        title: areaOverviewData.title,
      },
    };
    const { uuid: componentUuid } = areaOverviewData;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();
    return areaOverviewData;
  };

  const saveMarketStats = async () => {
    const payload = {
      isActive: marketStatsData.isActive,
      content: {
        eyebrow: marketStatsData.eyebrow,
        body: marketStatsData.body,
        imageUrl: marketStatsData.imageUrl,
        stat1Value: marketStatsData.stat1Value,
        stat2Value: marketStatsData.stat2Value,
        stat3Value: marketStatsData.stat3Value,
      },
    };
    const { uuid: componentUuid } = marketStatsData;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();
    return marketStatsData;
  };

  const saveLinks = async () => {
    const payload = {
      isActive: linksData.isActive,
      content: {
        title: linksData.title,
        links: linksData.links,
      },
    };
    const { uuid: componentUuid } = linksData;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();
    return linksData;
  };

  const saveFeaturedListings = async () => {
    let payload = {
      isActive: featuredListingsData.isActive,
    };
    const { uuid: componentUuid } = featuredListingsData;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();

    const contentComponentUuid = featuredListingsData.uuid;
    const bindingUuid = featuredListingsData.bindings[0].uuid;
    payload = { config: featuredListingsData.bindings[0].config };
    await dispatch(updateContentComponentBindings({ contentComponentUuid, bindingUuid, payload })).unwrap();
    return featuredListingsData;
  };

  const saveContactCTA = async () => {
    const payload = {
      isActive: contactCTAData.isActive,
      content: {
        kicker: contactCTAData.kicker,
        title: contactCTAData.title,
        buttonLabel: contactCTAData.buttonLabel,
        imageUrl: contactCTAData.imageUrl,
        tags: contactCTAData.tags || [],
      },
    };
    const { uuid: componentUuid } = contactCTAData;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();
    return contactCTAData;
  };

  // Mirror the image into the page settings blob so the index can render the row
  // thumbnail without fetching the page's content components. Takes the page snapshot to
  // mirror onto (not the `pageData` closure) so a prior save step in the same saveAll isn't
  // silently dropped — `pageData` itself never reflects an earlier step's own write.
  const mirrorFeaturedImageUrl = async (pageSnapshot) => {
    const data = withSeoTitleFallback({
      ...pageSnapshot,
      settings: { ...(pageSnapshot?.settings || {}), featuredImageUrl: featuredImageData.imageUrl },
    });
    await dispatch(updatePageSettings(data)).unwrap();
    return data;
  };

  const saveFeaturedImage = async (pageSnapshot) => {
    let data = {
      ...featuredListingsData.bindings[0].config.vars.requestBody,
      streetAddress: featuredListingsData.bindings[0].config.vars.requestBody?.streetAddress?.[0],
    };

    const primaryLinkResponse = await axios.post("/website/generate_search_uri", data, {
      headers: {
        "x-csrf-token": ReactOnRails.authenticityToken(),
        "Content-Type": "application/json",
      },
    });

    data = {
      ...featuredListingsData.bindings[0].config.vars.requestBody,
      streetAddress: featuredListingsData.bindings[0].config.vars.requestBody?.streetAddress?.[0],
      newListings: true,
    };

    const secondaryLinkResponse = await axios.post("/website/generate_search_uri", data, {
      headers: {
        "x-csrf-token": ReactOnRails.authenticityToken(),
        "Content-Type": "application/json",
      },
    });

    const primaryLink = primaryLinkResponse?.data?.data?.searchUri
      ?.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      ?.replace(/:(?=&)/g, "");

    const secondaryLink = secondaryLinkResponse?.data?.data?.searchUri
      ?.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      ?.replace(/:(?=&)/g, "");

    const payload = {
      content: {
        imageUrl: featuredImageData.imageUrl,
        primaryLink: {
          href: primaryLink,
          text: featuredImageData.primaryLink.text,
        },
        secondaryLink: {
          href: secondaryLink,
          text: featuredImageData.secondaryLink.text,
        },
      },
    };

    const { uuid: componentUuid } = featuredImageData;
    await dispatch(updateContentComponent({ uuid: componentUuid, payload })).unwrap();
    return mirrorFeaturedImageUrl(pageSnapshot);
  };

  // Both settings-chain saves take the accumulated page snapshot explicitly (see
  // mirrorFeaturedImageUrl above) instead of closing over `pageData`, for the same reason.
  const saveSeoData = async (pageSnapshot) => {
    const data = withSeoTitleFallback(pageSnapshot);
    await dispatch(updatePageSettings(data)).unwrap();
    return data;
  };

  const saveDetailsData = async (pageSnapshot) => {
    await dispatch(updatePage(pageSnapshot)).unwrap();
    return pageSnapshot;
  };

  const saveAll = async () => {
    setSaving(true);

    try {
      if (!isTokenValid(token, tokenExpiresAt)) {
        await dispatch(getToken()).unwrap();
      }

      // Every save* below returns the snapshot it actually sent, and saveAll uses those
      // returned snapshots — not the `areaOverviewData`-style closures — to declare the new
      // baseline. Each closure is safe to re-read on its own (nothing else in this function
      // mutates it mid-run), except the page/settings chain: saveSeoData, saveFeaturedImage
      // (via mirrorFeaturedImageUrl) and saveDetailsData all PUT/PATCH the same page+settings
      // resource from a `pageData`-shaped snapshot, so each one must build on what the
      // previous one in this chain actually persisted, not on the stale `pageData` closure.
      const savedAreaOverview = areaOverviewHasChanges ? await saveAreaOverview() : areaOverviewData;
      const savedMarketStats = marketStatsHasChanges ? await saveMarketStats() : marketStatsData;
      const savedLinks = linksHasChanges ? await saveLinks() : linksData;
      const savedFeaturedListings = featuredListingsHasChanges
        ? await saveFeaturedListings()
        : featuredListingsData;
      const savedContactCTA = contactCTADataHasChanges ? await saveContactCTA() : contactCTAData;

      let savedPageData = pageData;
      if (pageDataHasChanges) savedPageData = await saveSeoData(savedPageData);
      if (featuredImageHasChanges) {
        savedPageData = await saveFeaturedImage(savedPageData);
      } else if (
        featuredImageData?.imageUrl &&
        featuredImageData.imageUrl !== pageData?.settings?.featuredImageUrl
      ) {
        // Existing pages never dirty the Featured Image panel without an upload; mirror the
        // resolved image (template default included) so the index thumbnail still appears.
        savedPageData = await mirrorFeaturedImageUrl(savedPageData);
      }
      if (pageDataHasChanges) savedPageData = await saveDetailsData(savedPageData);

      await dispatch(getPageContentComponents()).unwrap();

      setAreaOverviewData(savedAreaOverview);
      setOriginalAreaOverviewData(savedAreaOverview);
      setMarketStatsData(savedMarketStats);
      setOriginalMarketStatsData(savedMarketStats);
      setLinksData(savedLinks);
      setOriginalLinksData(savedLinks);
      setFeaturedListingsData(savedFeaturedListings);
      setOriginalFeaturedListingsData(savedFeaturedListings);
      setContactCTAData(savedContactCTA);
      setOriginalContactCTAData(savedContactCTA);
      setOriginalFeaturedImageData(featuredImageData);
      setPageData(savedPageData);
      setOriginalPageData(savedPageData);

      return savedPageData;
    } catch {
      dispatch(
        setSnackbar({
          show: true,
          position: "top",
          text: "Error saving page",
          schema: "error",
          timeout: 5000,
          closeOnTimeout: () => dispatch(setSnackbar({ show: false })),
        }),
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const onSaveClicked = async () => {
    await saveAll();
  };

  const onPublishedClicked = async () => {
    const savedPageData = await saveAll();
    // saveAll already surfaced the error snackbar; don't publish from a page that may not
    // have actually saved.
    if (!savedPageData) return;

    const data = withSeoTitleFallback({ ...savedPageData, status: "published" });

    await dispatch(updatePageSettings(data)).unwrap();
    await dispatch(publish()).unwrap();

    await dispatch(getPageSettings({ uuid })).unwrap();
  };

  if (saving || loading) return <Loading />;

  return (
    <div className="tw-flex-0 tw-max-w-[1416px] tw-mx-auto tw-p-[16px] tw-flex tw-flex-wrap tw-gap-[16px]">
      <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[16px]">
        <div className="tw-flex-0 tw-flex tw-justify-between tw-items-center tw-gap-[8px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-px-[20px] tw-py-[24px]">
          <div className="tw-flex tw-items-center tw-gap-[8px]">
            <IconButton schema="transparent" onClick={() => navigate("/area_pages")}>
              <ArrowLeftSolidV6 size="l" />
            </IconButton>
            {/* <Typography variant="headline1">{page.area ? "Edit Page" : "Page Details"}</Typography> */}
          </div>
          {saving && (
            <span className="tw-text-12d tw-flex tw-items-center tw-gap-[4px]">
              Saving... <SpinnerThirdSolidV6 className="tw-animate-spin" />
            </span>
          )}
          {!saving && hasChanges && (
            <span className="tw-text-[#e5941b] tw-text-12d tw-flex tw-items-center tw-gap-[4px]">
              Unsaved changes <TriangleExclamationSolidV6 />
            </span>
          )}
          {!saving && !hasChanges && (
            <span className="tw-text-semantic-green-110 tw-text-12d tw-flex tw-items-center tw-gap-[4px]">
              Saved <CheckSolidV6 />
            </span>
          )}
        </div>

        {/* Left column: the page's content sections. Editing any section updates `page`,
            which is what flags the page as having unsaved changes. */}
        <div className="tw-flex tw-flex-col tw-gap-[16px]">
          <AreaOverviewPanel data={areaOverviewData} setAreaOverviewData={setAreaOverviewData} />
          <MarketStatsPanel data={marketStatsData} setMarketStatsData={setMarketStatsData} />
          <FeaturedListingsPanel
            data={featuredListingsData}
            setFeaturedListingsData={setFeaturedListingsData}
          />
          {linksData && <LinksPanel data={linksData} setLinksData={setLinksData} />}
          <ContactFormPanel data={contactCTAData} setContactCTAData={setContactCTAData} />
        </div>
      </div>

      {/* Right column panels. Each keeps its own local state for now; wiring them to `page` is a follow-up. */}
      <div className="tw-flex tw-flex-col tw-gap-[16px] tw-max-w-[300px]">
        <PublishPanel
          hasChanges={hasChanges}
          data={pageSettings}
          isArchived={pageData?.isActive === false}
          onSaveClicked={() => onSaveClicked()}
          onPublishedClicked={() => onPublishedClicked()}
        />
        <DetailsPanel data={pageData} setDetailsData={setPageData} />
        <FeaturedImagePanel data={featuredImageData} setFeaturedImageData={setFeaturedImageData} />
        <ThumbnailPanel />
        <SeoPanel data={pageData} setSeoData={setPageData} />
      </div>

      <UnsavedChangesModal blocker={blocker} onSave={onSaveClicked} />
    </div>
  );
};

export default AreaPageEditor;
